import { FieldModel, ProgramBenchmarkName, ProgramTask, TaskDataTraitStatus } from '@cibo/core'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Lock from '@mui/icons-material/Lock'
import { Box, IconButton, Stack, Tooltip } from '@mui/material'
import { MouseEvent, ReactNode, useEffect, useState } from 'react'
import { InfoNeededCorner } from '../../../../../components/TraitTable/InfoNeededCorner'
import { useProgramEngagementContext } from '../../../../../context'
import { TraitEditorModal } from './TraitEditorModal'

interface TraitCellContainerProps {
  value: any
  children: ReactNode
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName; traits: TaskDataTraitStatus[] }>
  year?: number
  selectedField?: FieldModel
  traitId: string
  onAdd?: () => void
  onSaveSuccess?: () => void
  onDelete?: () => void
}

export const TraitCellContainer = ({
  value,
  children,
  onAdd,
  onSaveSuccess,
  onDelete,
  task,
  selectedField,
  year,
  traitId,
}: TraitCellContainerProps) => {
  const { engagement, program } = useProgramEngagementContext()
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [optimisticInfoNeeded, setOptimisticInfoNeeded] = useState<boolean | null>(null)

  const ruleParams = task.rule?.params || {}

  const benchmarkName = ruleParams.benchmarkName
  const benchmarkRequirements = program.data?.benchmarks?.find(
    benchmark => benchmark.benchmark === benchmarkName
  )?.details

  const relevantRequirement =
    benchmarkRequirements &&
    benchmarkRequirements.find(
      (req: any) =>
        req.traitId === traitId && (year === undefined ? req.year === undefined : req.year === year)
    )

  const infoNeededV2 =
    selectedField?.id && task && relevantRequirement
      ? engagement.data?.taskIsMissingFieldInfo(
          task.id,
          selectedField?.resourceId,
          relevantRequirement
        )
      : true

  useEffect(() => {
    setOptimisticInfoNeeded(null)
  }, [infoNeededV2])

  const effectiveInfoNeeded = optimisticInfoNeeded !== null ? optimisticInfoNeeded : infoNeededV2

  const handleAdd = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setIsEditorModalOpen(true)
    onAdd?.()
  }

  const onClose = () => {
    setIsEditorModalOpen(false)
    setAnchorEl(null)
  }

  const handleSuccess = () => {
    onClose()
    setOptimisticInfoNeeded(false)
    onSaveSuccess?.()
  }

  if (!value) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: 75,
            position: 'relative',
          }}
        >
          {effectiveInfoNeeded && (
            <InfoNeededCorner style={{ position: 'absolute', top: 0, left: 0 }} />
          )}
          <IconButton size="small" onClick={handleAdd}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        <TraitEditorModal
          isOpen={isEditorModalOpen}
          onClose={onClose}
          onSuccess={handleSuccess}
          anchorEl={anchorEl}
          traitId={traitId}
          year={year}
          selectedField={selectedField}
          task={task}
        />
      </>
    )
  }

  if (value.immutable) {
    return (
      <Box
        sx={{
          height: 75,
          backgroundColor: '#18303814',
          borderRadius: '4px',
          m: 1,
          p: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          width: '100%',
        }}
      >
        <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
          {children}
          <Lock fontSize="small" color="disabled" sx={{ opacity: '0.5' }} />
        </Stack>
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          px: 1,
          position: 'relative',
          height: 75,
          '&:hover .cell-edit-overlay': { opacity: 1 },
        }}
      >
        {effectiveInfoNeeded && (
          <InfoNeededCorner style={{ position: 'absolute', top: 0, left: 0 }} />
        )}
        <Box sx={{ flex: 1 }}>{children}</Box>
        <Stack
          className="cell-edit-overlay"
          direction="column"
          spacing={0.25}
          sx={{ opacity: 0, transition: 'opacity 0.15s' }}
        >
          <IconButton
            size="small"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation()
              handleAdd(e)
            }}
          >
            <EditIcon fontSize="small" color="action" />
          </IconButton>
          {onDelete && (
            <Tooltip title="delete detail">
              <span>
                <IconButton
                  size="small"
                  aria-label="delete detail"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation()
                    setOptimisticInfoNeeded(true)
                    onDelete()
                  }}
                >
                  <DeleteIcon fontSize="small" color="action" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Box>
      <TraitEditorModal
        isOpen={isEditorModalOpen}
        onClose={onClose}
        onSuccess={handleSuccess}
        anchorEl={anchorEl}
        traitId={traitId}
        year={year}
        selectedField={selectedField}
        task={task}
      />
    </>
  )
}
